import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { AdminLayout } from "../../layout/AdminLayout";
import ResourceCard from "../../../public/components/ResourceCard";
import { inquiriesService } from "../../../services/inquiriesService";

export const InquiryView = () => {
	const [inquiry, setInquiry] = useState(null);
	const [loading, setLoading] = useState(true);
	const [deleting, setDeleting] = useState(false);
	const { id } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		loadInquiry();
	}, [id]);

	const loadInquiry = async () => {
		try {
			setLoading(true);
			const data = await inquiriesService.getById(id);
			setInquiry(data || null);
		} catch {
			toast.error("Failed to load inquiry entry");
			navigate("/admin/inquiries");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async () => {
		if (!inquiry) return;
		if (
			!window.confirm("Are you sure you want to delete this inquiry entry?")
		) {
			return;
		}

		try {
			setDeleting(true);
			await inquiriesService.delete(inquiry._id);
			toast.success("Inquiry entry deleted");
			navigate("/admin/inquiries");
		} catch {
			toast.error("Failed to delete inquiry entry");
		} finally {
			setDeleting(false);
		}
	};

	return (
		<AdminLayout>
			<div className="p-6">
				<div className="max-w-6xl mx-auto">
					<div className="flex items-center justify-between mb-6">
						<div className="flex items-center gap-3">
							<Link
								to="/admin/inquiries"
								className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
							>
								<ArrowLeft size={18} />
								Back to Inquiries
							</Link>
						</div>

						{inquiry && (
							<button
								onClick={handleDelete}
								disabled={deleting}
								className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 text-white px-4 py-2 rounded-lg transition"
							>
								<Trash2 size={18} />
								{deleting ? "Deleting..." : "Delete"}
							</button>
						)}
					</div>

					<div className="bg-white rounded-lg shadow p-6">
						{loading ? (
							<p className="text-gray-500">Loading inquiry entry...</p>
						) : !inquiry ? (
							<p className="text-gray-500">Inquiry entry not found.</p>
						) : (
							<div className="space-y-6">
								<h1 className="text-3xl font-bold text-gray-800">
									Inquiry Entry
								</h1>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-semibold text-gray-600 mb-1">
											Name
										</label>
										<p className="text-gray-900">{inquiry.name}</p>
									</div>
									<div>
										<label className="block text-sm font-semibold text-gray-600 mb-1">
											Email
										</label>
										<p className="text-gray-900">{inquiry.email}</p>
									</div>
									<div>
										<label className="block text-sm font-semibold text-gray-600 mb-1">
											Company
										</label>
										<p className="text-gray-900">{inquiry.company}</p>
									</div>
									<div>
										<label className="block text-sm font-semibold text-gray-600 mb-1">
											Submitted At
										</label>
										<p className="text-gray-900">
											{inquiry.createdAt
												? new Date(inquiry.createdAt).toLocaleString()
												: "-"}
										</p>
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-600 mb-1">
										Message
									</label>
									<div className="bg-gray-50 border border-gray-200 rounded-lg p-4 whitespace-pre-wrap text-gray-900">
										{inquiry.message}
									</div>
								</div>

								<div>
									<h2 className="text-xl font-bold text-gray-900 mb-3">
										Selected Resources ({inquiry.resources_count || 0})
									</h2>
									{!inquiry.resources || inquiry.resources.length === 0 ? (
										<p className="text-gray-500">
											No resources found for this inquiry.
										</p>
									) : (
										<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
											{inquiry.resources.map((resource) => (
												<div key={resource.id} className="space-y-2">
													<ResourceCard
														resource={resource}
														showContactDetails
													/>
													{resource.inquiry_comment ? (
														<div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
															<p className="text-xs font-semibold text-blue-700 mb-1">
																Inquiry Comment
															</p>
															<p className="text-sm text-blue-900 whitespace-pre-wrap">
																{resource.inquiry_comment}
															</p>
														</div>
													) : null}
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</AdminLayout>
	);
};
